/**
 * Custom hook: useWorkflowDefinitionData
 *
 * This hook centralizes the logic for fetching and managing workflow definition data.
 * It retrieves workflow names, versions, and definitions using dedicated data hooks,
 * and provides computed values and utility functions for workflow selection and versioning.
 * Useful for workflow editors, viewers, and any component needing workflow metadata.
 */
import { useMemo, useEffect, useCallback } from "react";
import { useRouteMatch } from "react-router-dom";
import _ from "lodash";
import {
  useLatestWorkflowDefs,
  useWorkflowDef,
  useWorkflowNamesAndVersions,
  useWorkflowVersions,
} from "../data/workflow";

import { NEW_WORKFLOW_TEMPLATE } from "../schema/workflow";

export const useWorkflowDefinitionData = () => {
  const match = useRouteMatch();
  const workflowName = _.get(match, "params.name");
  const selectedVersion = useMemo(
    () => _.get(match, "params.version"),
    [match]
  );
  // Data fetching hooks
  const { data: namesAndVersions, refetch: refetchNamesAndVersions } =
    useWorkflowNamesAndVersions();

  const { data: wfVersions, refetch: refetchwfVersions } =
    useWorkflowVersions(workflowName);

  const { data: wfData, refetch } = useLatestWorkflowDefs();
  // console.log(wfData);
  // Computed values
  const versions = useMemo(
    () => (wfVersions ? [...wfVersions].sort((a, b) => b - a) : []),
    [wfVersions]
  );

  const getLatestVersion = useCallback(() => {
    if (versions.length > 0) {
      return versions[0];
    }
    return null;
  }, [versions]);

  const validSelectedVersion = useMemo(() => {
    if (!versions || versions.length === 0) return false;
    // Convert selectedVersion to number for comparison
    const numericVersion = parseInt(selectedVersion);
    return versions.includes(numericVersion);
  }, [versions, selectedVersion]);

  const workflowVersion = useMemo(() => {
    if (!_.isUndefined(selectedVersion) && workflowName) {
      return validSelectedVersion ? selectedVersion : getLatestVersion();
    }
    return null;
  }, [
    selectedVersion,
    workflowName,
    validSelectedVersion,
    getLatestVersion,
    versions,
  ]);
  // Workflow definition data

  const {
    data: workflowDef,
    isFetching,
    refetch: refetchWorkflow,
  } = useWorkflowDef(workflowName, workflowVersion, NEW_WORKFLOW_TEMPLATE);

  // Auto-refetch when workflowName changes
  useEffect(() => {
    refetchwfVersions();
  }, [workflowName]);

  useEffect(() => {
    refetch();
  }, []);

  return {
    // Raw data
    namesAndVersions,
    wfVersions,
    wfData,
    workflowDef,
    isFetching,

    // Computed values
    versions,
    selectedVersion,
    validSelectedVersion,
    // latestVersion,
    workflowVersion,
    workflowName,
    match,

    // Version utilities
    getLatestVersion,

    // Refetch functions
    refetchNamesAndVersions,
    refetchwfVersions,
    refetch,
    refetchWorkflow,
  };
};
